import { authStore, sessionStore, type IdTokenClaims, type RoleProfile } from '@zagru/platform';

export const MOCK_PROFILE_KEY = 'zagru.mock.profile';
export const MOCK_SESSION_KEY = 'zagru.mock.session';

export function isMockMode(): boolean {
  return import.meta.env.VITE_MOCK_API === 'true' || (typeof window !== 'undefined' && window.__ZAGRU_CONFIG__?.mockApi === true);
}

function base64url(input: string): string {
  return btoa(unescape(encodeURIComponent(input))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Creates an unsigned JWT carrying the Zagru claims (mock mode only — never accepted by a real backend). */
export function createMockJwt(profile: RoleProfile): string {
  const now = Math.floor(Date.now() / 1000);
  const claims: IdTokenClaims = { sub: `usr-${profile}`, iss: 'mock', aud: 'zagru-one-web', iat: now, exp: now + 8 * 3600, name: profile, preferred_username: profile, zagru_tenant: '0192a000-0000-7000-8000-000000000001' };
  return `${base64url(JSON.stringify({ alg: 'none', typ: 'JWT' }))}.${base64url(JSON.stringify(claims))}.mock`;
}

/** Signs in with a fake token in mock mode; the MSW /me handler builds the session from the stored profile. */
export function mockSignIn(profile: RoleProfile): void {
  try {
    localStorage.setItem(MOCK_PROFILE_KEY, profile);
    localStorage.setItem(MOCK_SESSION_KEY, '1');
  } catch {
    /* ignore */
  }
  const token = createMockJwt(profile);
  const claims = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as IdTokenClaims;
  authStore.getState().setTokens({ accessToken: token, expiresAt: Date.now() + 8 * 3600 * 1000, tokenType: 'Bearer' }, claims);
  authStore.getState().setStatus('authenticated');
}

export function mockSignOut(): void {
  try {
    localStorage.removeItem(MOCK_SESSION_KEY);
  } catch {
    /* ignore */
  }
  sessionStore.getState().clear();
  authStore.getState().clear();
  authStore.getState().setStatus('anonymous');
}

/** Restores a mock session on reload. */
export function restoreMockSession(): boolean {
  try {
    if (localStorage.getItem(MOCK_SESSION_KEY) === '1') {
      const profile = (localStorage.getItem(MOCK_PROFILE_KEY) as RoleProfile | null) ?? 'supervisor';
      mockSignIn(profile);
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}
